/**
 * MountedFileContextMenu
 *
 * Context menu for .sql files inside a mounted folder tree:
 * Open, Pin, Copy File Path, Reveal in Finder.
 */

import type { ReactNode } from 'react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '../../../components/ui/context-menu';
import { toast } from '../../../components/ui/toaster';
import { writeClipboardText } from '../../../lib/clipboard';
import { useScriptBrowserStore } from '../store/scriptBrowserStore';

type MountedFileContextMenuProps = {
  filePath: string;
  onOpen: () => void;
  children: ReactNode;
};

const isMac =
  typeof navigator !== 'undefined' &&
  navigator.platform?.toLowerCase().includes('mac');

export function MountedFileContextMenu({
  filePath,
  onOpen,
  children,
}: MountedFileContextMenuProps) {
  const scripts = useScriptBrowserStore((s) => s.scripts);
  const togglePin = useScriptBrowserStore((s) => s.togglePin);

  const handleCopyPath = async () => {
    const ok = await writeClipboardText(filePath);
    if (ok) {
      toast.success({ title: 'Path copied to clipboard' });
    } else {
      toast.error({ title: 'Failed to copy path' });
    }
  };

  const handleReveal = async () => {
    if (!window.electronAPI?.showItemInFolder) return;
    const result = await window.electronAPI.showItemInFolder(filePath);
    if (!result.success) {
      toast.error({ title: 'Could not reveal file', description: result.error });
    }
  };

  const handlePin = () => {
    const normalized = filePath.replace(/\\/g, '/').trim();
    const script = scripts.find(
      (s) => s.filePath.replace(/\\/g, '/').trim() === normalized
    );
    if (script) {
      togglePin(script.id);
    } else {
      toast.default({ title: 'Open the file first to pin it' });
    }
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onSelect={onOpen}>Open</ContextMenuItem>
        <ContextMenuItem onSelect={handlePin}>Pin</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onSelect={handleCopyPath}>
          Copy File Path
        </ContextMenuItem>
        <ContextMenuItem onSelect={handleReveal}>
          {isMac ? 'Reveal in Finder' : 'Show in Explorer'}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
